import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useState } from "react";

import PaginationFooter from "common/components/PaginationFooter";
import {
  Table,
  TableCell,
  TableHead,
  TableRow,
} from "common/components/ui/TableV2";
import usePageSize from "common/hooks/usePageSize";

interface SimpleDataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  localStorageId: string;
}

export default function SimpleDataTable<TData, TValue>({
  columns,
  data,
  localStorageId,
}: SimpleDataTableProps<TData, TValue>) {
  const { pageSize, onPageSizeChange } = usePageSize(localStorageId);
  const [pageIndex, setPageIndex] = useState(0);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: { pagination: { pageIndex, pageSize } },
    autoResetPageIndex: false,
    onPaginationChange: (updaterOrValue) => {
      const next =
        typeof updaterOrValue === "function"
          ? updaterOrValue({ pageIndex, pageSize })
          : updaterOrValue;

      setPageIndex(next.pageIndex);
      onPageSizeChange(next.pageSize);
    },
  });

  return (
    <>
      <Table>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  className={header.column.columnDef.meta?.className}
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </TableHead>
              ))}
            </tr>
          ))}
        </thead>

        <tbody>
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={cell.id}
                  className={cell.column.columnDef.meta?.className}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </tbody>
      </Table>

      <PaginationFooter
        selectedPage={pageIndex}
        setSelectedPage={(page) => table.setPageIndex(page)}
        pageSize={pageSize}
        setPageSize={(newPageSize) => table.setPageSize(newPageSize)}
        totalItemCount={data.length}
        isLastPageHidden={false}
      />
    </>
  );
}
